function findOldestEntryIndex(array) {
    // First priority: Find entries without timestamps (considered oldest)
    for (let i = 0; i < array.length; i++) {
        if (!array[i].timeStamp) {
            return i;
        }
    }
    
    // Second priority: If all entries have timestamps, find the one with oldest timestamp
    let oldestIndex = -1;
    let oldestTimestamp = Infinity;
    
    array.forEach((item, index) => {
        if (item.timeStamp) {
            const timestamp = new Date(item.timeStamp).getTime();
            if (timestamp < oldestTimestamp) {
                oldestTimestamp = timestamp;
                oldestIndex = index;
            }
        }
    });
    
    return oldestIndex;
}

// An entry the form has not filled in yet has no amount at all. Reading it raw
// makes the sum NaN, and the balancing branches below then write that NaN back
// onto an entry — so one missing field poisons the whole array instead of being
// corrected by it.
const amountOf = (obj, field) => Number.isFinite(obj?.[field]) ? obj[field] : 0

function verifyAmountSmart(amount, array, arrayField) {
    // Nothing to balance against: leave the array alone rather than distributing
    // an undefined target across it.
    if (!Number.isFinite(amount)) {
        return array;
    }

    // Calculate the current sum of the arrayField values
    const currentSum = array.reduce((sum, obj) => sum + amountOf(obj, arrayField), 0);

    // If the current sum equals the desired amount, return the array as is
    if (currentSum === amount || array.length === 0) {
        return array;
    }

    // Create a copy of the array to avoid mutation
    const resultArray = [...array];

    // Find the oldest entry (prioritizing entries without timestamps)
    const oldestIndex = findOldestEntryIndex(array);

    if (currentSum > amount) {
        // OVER TARGET: Prioritize oldest entry (no timestamp > oldest timestamp), otherwise first line
        const excess = currentSum - amount;
        
        let targetIndex = 0;
        if (oldestIndex !== -1) {
            // Found oldest entry (prioritizing entries without timestamps)
            targetIndex = oldestIndex;
        }
        
        resultArray[targetIndex] = {
            ...resultArray[targetIndex],
            [arrayField]: Math.max(0, amountOf(resultArray[targetIndex], arrayField) - excess)
        };
    } else {
        // UNDER TARGET: Prioritize oldest entry (no timestamp > oldest timestamp), otherwise last line
        const shortfall = amount - currentSum;
        
        let targetIndex = resultArray.length - 1;
        if (oldestIndex !== -1) {
            // Found oldest entry (prioritizing entries without timestamps)
            targetIndex = oldestIndex;
        }

        resultArray[targetIndex] = {
            ...resultArray[targetIndex],
            [arrayField]: amountOf(resultArray[targetIndex], arrayField) + shortfall
        };
    }

    return resultArray;
}

/**
 * Recalculates the base currency equivalent for all items in an array using a provided exchange rate.
 * @param {Array<Object>} array - The array of sub-entity items (e.g., payments).
 * @param {number} exchangeRate - The rate to use for conversion (Foreign Currency to Base Currency).
 * @param {string} arrayField - The name of the field containing the foreign currency amount.
 * @param {string} baseCurrencyField - The name of the field to update with the base currency amount.
 * @returns {Array<Object>} A new array with the base currency field updated.
 */
function recalculateBaseCurrency(array, exchangeRate, arrayField, baseCurrencyField) {
    
    // Check for a valid exchange rate just once before mapping
    if (typeof exchangeRate !== 'number' || exchangeRate <= 0) {
        console.warn('Invalid or missing exchange rate. Skipping base currency recalculation.');
        return array; 
    }

    return array.map(item => {
        // Only proceed if the primary amount field exists on the item
        if (item[arrayField] !== undefined) {
            
            // Recalculate: Foreign Currency Amount / Exchange Rate
            const newBaseAmount = item[arrayField] / exchangeRate;
            
            return {
                ...item,
                // Update the base currency field
                [baseCurrencyField]: newBaseAmount,
                "exchangeRate": exchangeRate
            };
        }
        return item; // Return item as is if the primary amount field is missing
    });
}


const subCheckTotal = (data, operation) => {

    if (!data[operation.subEntity]) {
        return data
    }

    const amount = data[operation.field]
    const array = data[operation.subEntity]
    const arrayField = operation.subEntityField

    // 1. Get the exchange rate and base currency field name from the operation config
    const exchangeRate = data[operation.fxField]; 
    const baseCurrencyField = operation.subEntityFieldBaseCurrency;

    const verifiedArray = verifyAmountSmart(amount, array, arrayField)

    // 2. Recalculate the base currency using the dedicated function
    const recalculatedArray = recalculateBaseCurrency(
        verifiedArray, 
        exchangeRate, 
        arrayField, 
        baseCurrencyField
    );

    // Return new object instead of mutating
    return {
        ...data,
        [operation.subEntity]: recalculatedArray,
        [operation.fieldCount]: recalculatedArray.length
    }
}

export default subCheckTotal
